<script setup lang="ts">
/**
 * Floating status pill for in-flight API actions (and their failures) —
 * M3-snackbar shaped, sits above the bottom nav. One per view; `label` null
 * hides it. Kept above the detail overlay (z-60) and action sheet (z-70) so
 * feedback is visible no matter where the action started.
 */
defineProps<{
  label: string | null;
  error?: boolean;
}>();
</script>

<template>
  <Teleport to="body">
    <Transition name="busy-toast">
      <div
        v-if="label"
        class="fixed bottom-[92px] left-1/2 z-[80] flex -translate-x-1/2 items-center gap-2 whitespace-nowrap rounded-full px-4 py-2.5 font-body-sm"
        :class="error ? 'bg-error text-on-error' : 'bg-inverse-surface text-inverse-on-surface'"
        role="status"
        aria-live="polite"
      >
        <span v-if="error" class="material-symbols-outlined text-[16px]">error</span>
        <span v-else class="spin" aria-hidden="true" />
        {{ label }}
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.busy-toast-enter-active,
.busy-toast-leave-active {
  transition:
    opacity 200ms cubic-bezier(0.25, 1, 0.5, 1),
    transform 200ms cubic-bezier(0.25, 1, 0.5, 1);
}
.busy-toast-enter-from,
.busy-toast-leave-to {
  opacity: 0;
  transform: translate(-50%, 8px);
}
.busy-toast-enter-to,
.busy-toast-leave-from {
  transform: translate(-50%, 0);
}
@media (prefers-reduced-motion: reduce) {
  .busy-toast-enter-active,
  .busy-toast-leave-active {
    transition: opacity 200ms;
  }
  .busy-toast-enter-from,
  .busy-toast-leave-to {
    transform: translate(-50%, 0);
  }
}
</style>
